// The Library: ideas, stories, testimonials and objections, jotted in seconds
// and picked from by the tools that write (supabase/0022_library.sql).
//
// One table, one list. A kind's unused fields stay null. Nothing here knows
// which tool picks an item — that tool writes a `usedByStamp` into `usedBy`.
//
// `photo` (0023) is a testimonial's headshot, as a link she pastes. It becomes
// a pick from the GHL image library once that connection exists — the column is
// the same either way, which is why it is here before the picker is.
//
// USED MANY TIMES (her call, 2026-09-17). `usedBy` is an ARRAY of stamps. It
// used to be one object on the old `ideas` table, and an idea vanished from
// every picker once used. Now a used item stays pickable and says where it
// went. `usedList` reads either shape, so a row written before 0022's copy —
// or by an older browser tab — can never crash a list or read as unused.

import { createRecordStore } from "./recordStore";

// `id`, `createdAt`, `updatedAt` are handled by recordStore — not listed.
export const LIBRARY_FIELDS = [
  "kind", "text", "title", "tag", "whose", "who", "result", "answer", "permission", "photo", "usedBy",
];

/* SHARED (her call, 2026-09-28: "the Library is very important, it needs to
   lazy load" — it was slow to open). The Library screen, the post screen and
   every picker read ONE load per visit, and a save anywhere reaches all of
   them. The Library screen asks for `{ refresh: true }`: it shows what is
   already here at once and quietly reloads behind it. */
export const useLibrary = createRecordStore({
  table: "library_items",
  fields: LIBRARY_FIELDS,
  idPrefix: "lib",
  shared: true,
});

/* In the order the quick-add chips and the filter row show them. `textLabel`
   names the main field in the open item; `tagLabel` names the `tag` column
   for that kind (an idea's pillar is picked from her pillars, not typed). */
export const LIBRARY_KINDS = [
  { id: "idea",        label: "Idea",        plural: "Ideas",        textLabel: "Idea",      tagLabel: "Pillar" },
  { id: "story",       label: "Story",       plural: "Stories",      textLabel: "Story",     tagLabel: "Themes" },
  { id: "testimonial", label: "Testimonial", plural: "Testimonials", textLabel: "Quote",     tagLabel: null },
  { id: "objection",   label: "Objection",   plural: "Objections",   textLabel: "Objection", tagLabel: "Tags" },
];

export const kindOf = (id) => LIBRARY_KINDS.find((k) => k.id === id) || LIBRARY_KINDS[0];

export const STORY_MINE = "Mine";
export const STORY_CLIENT = "A client's";

/** Every place an item was used, whatever shape `usedBy` arrived in. */
export function usedList(item) {
  const u = item?.usedBy;
  if (!u) return [];
  if (Array.isArray(u)) return u.filter(Boolean);
  return typeof u === "object" ? [u] : [];
}

export const isUnused = (item) => usedList(item).length === 0;

/** "Unused" or "Used in 2" — the label every list and picker shows. */
export function usedLabel(item) {
  const n = usedList(item).length;
  return n ? `Used in ${n}` : "Unused";
}

/** "Instagram post · March newsletter", for the hover and the open item. */
export function usedWhere(item) {
  return usedList(item).map((s) => s.label || "Untitled").join(" · ");
}

/** The stamp a picking tool writes. One shape for every caller. */
export function usedByStamp(type, id, label) {
  return { type, id: id || null, label: label || null };
}

/** Add a use. Returns the new array; never mutates. */
export function addUse(item, stamp) {
  return [...usedList(item), stamp];
}

/**
 * Point a PROVISIONAL use at the thing that now exists. A tool stamps
 * `{type, id: null}` the moment she picks, because the post is not saved yet;
 * on save it becomes the real id. Saving the same post again only refreshes
 * its label — it never counts as a second use. An item already used in three
 * other posts keeps all three.
 */
/* The NEWEST unsaved stamp of a type. Newest, not first: the old idea board
   left unsaved stamps behind (a pick whose post was never saved), and those
   were copied across by 0022 — finishing or undoing the first one would touch
   that leftover and leave today's pick dangling. */
function lastProvisional(list, type) {
  for (let i = list.length - 1; i >= 0; i--) if (list[i].type === type && !list[i].id) return i;
  return -1;
}

export function confirmUse(item, type, id, label) {
  const list = usedList(item);
  const stamp = usedByStamp(type, id, label);
  const same = list.findIndex((s) => s.type === type && id && s.id === id);
  if (same >= 0) return list.map((s, i) => (i === same ? stamp : s));
  const provisional = lastProvisional(list, type);
  if (provisional >= 0) return list.map((s, i) => (i === provisional ? stamp : s));
  return [...list, stamp];
}

/**
 * Take a use back — she picked, then put it back. `id` null takes back the
 * provisional stamp; an id takes back that saved thing's stamp. Only one.
 */
export function releaseUse(item, type, id = null) {
  const list = usedList(item);
  const i = id ? list.findIndex((s) => s.type === type && s.id === id) : lastProvisional(list, type);
  if (i < 0) return list;
  return [...list.slice(0, i), ...list.slice(i + 1)];
}

/**
 * Record a use that is already final — a quote pasted into a page, a story
 * handed to Brill for a deck. Picking the same item into the SAME record again
 * only refreshes its label, so a second pick is not a second use.
 */
export function noteUse(item, stamp) {
  const list = usedList(item);
  const i = list.findIndex((s) => s.type === stamp.type && s.id && s.id === stamp.id);
  if (i >= 0) return list.map((s, n) => (n === i ? stamp : s));
  return [...list, stamp];
}

/** A testimonial that cannot go on a public page or a deck yet. */
export const needsPermission = (item) => item?.kind === "testimonial" && !item.permission;

/* WHAT A PICKED LIBRARY ITEM SAYS IN "What's it about?" — its words, and
   what kind of thing they are, so Brill treats a story as a real story (the
   no-invented-clients rule lets a story she gave for THIS post through) and an
   objection as something to answer. Here, not in a panel file, since an arc's
   posts are handed their items the same way (2026-09-30). */
export function aboutFromLibrary(it) {
  const text = String(it?.text || "").trim();
  if (it?.kind === "story") return `${it.whose === STORY_CLIENT ? "A real client story" : "My story"}${it.title ? ` (${it.title})` : ""}: ${text}`;
  if (it?.kind === "testimonial") return `What a client said${it.who ? ` (${it.who})` : ""}: "${text}"${it.result ? ` — ${it.result}` : ""}`;
  if (it?.kind === "objection") return `The objection to answer: ${text}${it.answer ? `\nMy answer: ${it.answer}` : ""}`;
  return it?.title ? `${it.title}: ${text}` : text;
}

/** The line under an item's text in a list. */
export function metaFor(item) {
  if (item.kind === "idea") return item.tag || "Unfiled";
  if (item.kind === "story") return [item.whose || STORY_MINE, item.tag].filter(Boolean).join(" · ");
  if (item.kind === "testimonial") return item.who || "Who said it?";
  return item.tag || "Untagged";
}

/**
 * The list as the Library shows it.
 * @param kind "all" or a kind id
 * @param sort "newest" | "unused" — Unused puts unused first, newest within
 * @param q    search text, matched against the text and the meta line
 */
export function libraryView(items, { kind = "all", sort = "newest", q = "" } = {}) {
  const needle = q.trim().toLowerCase();
  const newest = (a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || ""));
  return (items || [])
    .filter((it) => kind === "all" || it.kind === kind)
    .filter((it) => !needle
      || String(it.text || "").toLowerCase().includes(needle)
      || metaFor(it).toLowerCase().includes(needle)
      || String(it.title || "").toLowerCase().includes(needle))
    .sort((a, b) => {
      if (sort === "unused") {
        const d = (isUnused(a) ? 0 : 1) - (isUnused(b) ? 0 : 1);
        if (d) return d;
      }
      return newest(a, b);
    });
}

export function libraryCounts(items) {
  const out = { all: (items || []).length };
  LIBRARY_KINDS.forEach((k) => { out[k.id] = (items || []).filter((it) => it.kind === k.id).length; });
  return out;
}
